import {
  AbstractAnswer,
  AbstractProgram,
  ArticleType,
  mapToCard,
} from "@kvix/shared";
import { CardList } from "@kvix/ui";
import { Container, Divider, Grid, makeStyles } from "@material-ui/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router";
import {
  useFetchFeaturedProgramComments,
  useFetchProgram,
  useFetchRelatedPrograms,
} from "../../../hooks/http";
import { useAndCaptureParamsId } from "../../../hooks/useAndCaptureParamsId";
import { Footer } from "../../Footer";
import { Card } from "../../partials/Content/Card";
import { Page } from "../../partials/Page";
import { Partner } from "../../partials/Partner";
import { About } from "./About";
import { ProgramComments } from "./Comments";
import { ProgramDetails } from "./Details";
import { ProgramIntroductionVideo } from "./IntroductionVideo";
import { ProgramPoster } from "./ProgramPoster";

const useStyles = makeStyles((theme) => ({
  containerSpacing: {
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("lg")]: {
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(3),
    },
  },

  details: {
    flexGrow: 1,
    flexBasis: 300,
    flexShrink: 300,
  },
  video: {
    flexGrow: 5,
    flexBasis: 500,

    [theme.breakpoints.down("xs")]: {
      marginTop: -theme.spacing(4),
    },
  },
  instructorCard: {
    flexBasis: 0,
    flexGrow: 1,

    [theme.breakpoints.up("sm")]: {
      minWidth: 400,
    },
  },
}));

export default (props) => {
  const classes = useStyles(props);
  const programId = useAndCaptureParamsId("programId");
  const { t, ready } = useTranslation(["program", "seo"]);

  if (isNaN(programId)) {
    return <Redirect to="/page-not-found" />;
  }

  const program: AbstractProgram = useFetchProgram(+programId);
  const relatedPrograms: AbstractProgram[] = useFetchRelatedPrograms({
    id: +programId,
    max: 4,
  });

  const programComments: AbstractAnswer[] = useFetchFeaturedProgramComments({
    id: +programId,
  });

  const programReplays = useMemo(() => {
    if (program) {
      return program.sessions
        ?.filter((session) => session.hlsUrl || session.mp4Url)
        .sort(
          (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
        );
    }
  }, [program]);

  if (!ready || !program) {
    return null;
  }

  return (
    <Page
      meta={{
        title: t("seo:program.title", { title: program.title }),
        description: program.description,
        ogTitle: program.title,
        ogImage: program.posterUrl || program.imageUrl,
      }}
    >
      <Container className={classes.containerSpacing} maxWidth="lg">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={5} wrap="wrap-reverse">
              <Grid item className={classes.details}>
                <ProgramDetails
                  program={program}
                  replays={programReplays || []}
                />
              </Grid>

              <Grid
                title={program.title ? program.title : ""}
                item
                className={classes.video}
              >
                {program.videoUrl ? (
                  <ProgramIntroductionVideo program={program} />
                ) : (
                  <ProgramPoster program={program} />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <About
            program={program}
            replays={programReplays}
            classes={classes}
            view={"program"}
          />
        </Grid>
      </Container>

      <ProgramComments comments={programComments} />

      <Partner partnerProfile={program.instructor.partnerProfile} />

      <Container maxWidth="lg">
        <CardList title={t("relatedPrograms")} gutterBottom>
          {relatedPrograms.map((program) => (
            <Card
              type={ArticleType.PROGRAM}
              options={mapToCard({ program })}
              key={program.id}
            />
          ))}
        </CardList>
      </Container>

      <Footer />
    </Page>
  );
};
